import { useState, useRef } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useNavigate } from 'react-router-dom'
import { sanitizeInput, validateFormData, setCSRFToken } from '../utils/security'
import { appointmentService } from '../utils/api'
import './Appointment.css'

function Appointment() {
  const navigate = useNavigate()
  const recaptchaRef = useRef(null)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    serviceType: 'general',
    description: ''
  })

  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)
  const [recaptchaToken, setRecaptchaToken] = useState(null)

  useState(() => {
    setCSRFToken()
  }, [])

  const validationRules = {
    name: {
      required: true,
      label: 'Name',
      minLength: 2,
      maxLength: 50
    },
    email: {
      required: true,
      type: 'email'
    },
    phone: {
      required: true,
      type: 'phone'
    },
    preferredDate: {
      required: true,
      label: 'Preferred Date'
    },
    preferredTime: {
      required: true,
      label: 'Preferred Time'
    },
    serviceType: {
      required: true,
      label: 'Service Type'
    },
    description: {
      label: 'Description',
      maxLength: 500
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: sanitizeInput(value)
    }))
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const newErrors = validateFormData(formData, validationRules)
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    // Validate reCAPTCHA
    if (!recaptchaToken) {
      setSubmitStatus({
        type: 'error',
        message: 'Please complete the reCAPTCHA verification.'
      })
      return
    }

    setLoading(true)
    setSubmitStatus(null)

    try {
      // Store appointment data in session for payment page (include reCAPTCHA token)
      sessionStorage.setItem('pendingAppointment', JSON.stringify({ ...formData, recaptchaToken }))
      
      setSubmitStatus({
        type: 'success',
        message: 'Appointment request received! You will now proceed to payment.'
      })

      // Redirect to payment page after 2 seconds
      setTimeout(() => {
        navigate('/payment')
      }, 2000)
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Failed to process appointment. Please try again.'
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="appointment">
      <div className="container">
        <section className="appointment-intro">
          <h1>Book an Appointment</h1>
          <p>Schedule a consultation with our tax and accounting experts</p>
        </section>

        <div className="appointment-wrapper">
          <section className="appointment-info">
            <h2>Appointment Details</h2>
            <div className="info-card">
              <h3>📋 What to Expect</h3>
              <ul>
                <li>Discussion of your financial needs</li>
                <li>Personalized recommendations</li>
                <li>Answer any questions you have</li>
              </ul>
            </div>
            <div className="info-card">
              <h3>💰 Appointment Fee</h3>
              <p>A consultation fee of <strong>$100.00</strong> applies. Payment is required to confirm your appointment.</p>
            </div>
            <div className="info-card">
              <h3>📍 Meeting Format</h3>
              <p>We offer both in-person and virtual consultations. Our location is conveniently located in Rockyview County, Alberta.</p>
            </div>
          </section>

          <section className="appointment-form-section">
            <h2>Your Information</h2>

            {submitStatus && (
              <div className={`alert alert-${submitStatus.type}`}>
                {submitStatus.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="appointment-form">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={errors.name ? 'error' : ''}
                  disabled={loading}
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? 'error' : ''}
                  disabled={loading}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={errors.phone ? 'error' : ''}
                  placeholder="403-000-0000"
                  disabled={loading}
                />
                {errors.phone && <span className="error-message">{errors.phone}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="serviceType">Service Type *</label>
                <select
                  id="serviceType"
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="general">General Consultation</option>
                  <option value="corporate">Corporate Tax Return</option>
                  <option value="personal">Personal Tax Return</option>
                  <option value="bookkeeping">Bookkeeping Services</option>
                  <option value="planning">Tax Planning</option>
                  <option value="incorporation">Business Incorporation</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="preferredDate">Preferred Date *</label>
                  <input
                    type="date"
                    id="preferredDate"
                    name="preferredDate"
                    value={formData.preferredDate}
                    onChange={handleChange}
                    className={errors.preferredDate ? 'error' : ''}
                    disabled={loading}
                  />
                  {errors.preferredDate && <span className="error-message">{errors.preferredDate}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="preferredTime">Preferred Time *</label>
                  <input
                    type="time"
                    id="preferredTime"
                    name="preferredTime"
                    value={formData.preferredTime}
                    onChange={handleChange}
                    className={errors.preferredTime ? 'error' : ''}
                    disabled={loading}
                  />
                  {errors.preferredTime && <span className="error-message">{errors.preferredTime}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description">Additional Information</label>
                <textarea
                  id="description"
                  name="description"
                  rows="4"
                  value={formData.description}
                  onChange={handleChange}
                  className={errors.description ? 'error' : ''}
                  placeholder="Tell us more about your needs..."
                  disabled={loading}
                ></textarea>
                {errors.description && <span className="error-message">{errors.description}</span>}
              </div>

              <div className="form-group">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                  onChange={(token) => setRecaptchaToken(token)}
                  onExpired={() => setRecaptchaToken(null)}
                />
              </div>

              <button type="submit" className="btn btn-primary btn-large" disabled={loading}>
                {loading ? 'Processing...' : 'Continue to Payment'}
              </button>
            </form>
          </section>
        </div>
      </div>
    </div>
  )
}

export default Appointment
